import { useState, useEffect, useRef } from 'react'

/**
 * Hook to create a simple countdown timer.
 *
 * It takes a function `onFinish` which is called when the countdown is finished.
 * You can also specify the time in seconds for the countdown.
 * The hook returns an object with the single property `timeRemaining` which
 * is the remaining time of the countdown in seconds.
 *
 * The countdown is based on wall-clock time, so it stays accurate even when
 * the browser tab is in the background or timers are throttled.
 *
 * @example
 * const MyComponent = () => {
 *   const { timeRemaining } = useCountdown(() => {
 *     // Do something when the countdown is finished
 *   })
 *
 *   return (
 *     <div>
 *       Time remaining: {timeRemaining} seconds
 *     </div>
 *   )
 * }
 */
const useCountdown = (onFinish: () => void, timeInSeconds = 5) => {
	const [timeRemaining, setTimeRemaining] = useState(timeInSeconds)
	const onFinishRef = useRef(onFinish)

	useEffect(() => {
		onFinishRef.current = onFinish
	}, [onFinish])

	useEffect(() => {
		const endTime = Date.now() + timeInSeconds * 1000
		let intervalId: ReturnType<typeof setInterval> | undefined
		let hasFinished = false

		const updateTimeRemaining = () => {
			const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000))
			setTimeRemaining(remaining)

			if (remaining === 0 && !hasFinished) {
				hasFinished = true
				if (intervalId) {
					clearInterval(intervalId)
				}
				onFinishRef.current()
			}
		}

		updateTimeRemaining()
		intervalId = setInterval(updateTimeRemaining, 1000)

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				updateTimeRemaining()
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			if (intervalId) {
				clearInterval(intervalId)
			}
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [timeInSeconds])

	return { timeRemaining }
}

export default useCountdown
